import { useEffect, useRef, useState, type ReactNode } from "react";

export const PAGE = 20;

/**
 * A long list that draws a page at a time: the next page is added when the end scrolls near (or from the "Show more" button,
 * for keyboards and screen readers), so a phone with hundreds of sessions never builds hundreds of rows at once.
 */
export function PagedList<T>(props: { rows: T[]; render: (row: T) => ReactNode; page?: number; noun?: string; id?: string }) {
  const page = props.page ?? PAGE;
  const [count, setCount] = useState(page);
  const end = useRef<HTMLDivElement>(null);
  const remaining = props.rows.length - count;
  const more = () => setCount((c) => c + page);

  useEffect(() => {
    const el = end.current;
    if (!el || remaining <= 0 || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && more(), { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [remaining, count]);

  return (
    <>
      <div className="list" id={props.id}>
        {props.rows.slice(0, count).map(props.render)}
      </div>
      {remaining > 0 && (
        <>
          <div ref={end} aria-hidden />
          <button className="more-btn" onClick={more}>
            Show {Math.min(page, remaining)} more · {remaining} left{props.noun ? ` ${props.noun}` : ""}
          </button>
        </>
      )}
    </>
  );
}

/** Placeholder rows shaped like session rows, shown while a computer is still telling us its sessions. */
export function RowSkeletons({ n = 2 }: { n?: number }) {
  return (
    <div className="skeleton-list" aria-hidden>
      {Array.from({ length: n }, (_, k) => (
        <div className="skeleton" key={k}>
          <i style={{ width: k % 2 ? "36%" : "48%" }} />
          <i style={{ width: k % 2 ? "58%" : "70%", height: 9 }} />
        </div>
      ))}
    </div>
  );
}
